/**
 * La bajada a Free — `TDS-FREE-DOWNGRADE` §14, `OD-FREE-002` §5, §6 y §19.
 *
 * Cuando una suscripción de pago se cancela o caduca, el servidor PROGRAMA el
 * cambio a Free para una fecha y el cron lo hace solo cuando llega. Esta
 * tarjeta es lo único que el usuario puede hacer mientras tanto: elegir qué
 * clientes se quedan operativos. Si no elige, decide la selección automática
 * por actividad reciente, y no pasa nada malo: nadie tiene que entrar aquí
 * para que la bajada ocurra.
 *
 * ## Lo que esta tarjeta NO decide
 *
 * - **Ni la fecha, ni el límite, ni el orden**: llegan de la API. El orden de
 *   los candidatos es el de la selección automática, así que lo que se ve
 *   primero es lo que se quedaría sin hacer nada.
 * - **Ni archiva a nadie**: guarda una selección. El corte la aplica en la
 *   fecha, con lo que haya guardado entonces.
 * - **Ni si aún se puede cambiar**: `congelada` lo dice el servidor, y aunque
 *   la pantalla se equivocara, el PUT contestaría SELECTION_FROZEN.
 *
 * Sin bajada programada no enseña nada, salvo los avisos de después de una
 * bajada automática (§19.4) o de una restauración (§19.5). Quien no es OWNER
 * recibe un 403 y la tarjeta no aparece: la de «Tu plan» ya lo explica.
 */
import { useCallback, useEffect, useState } from "react";

import { api, type VistaDeBajada } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Casilla, Tarjeta } from "../ui/primitivas";

const tx = t.plan.bajada;

/** Los textos de OD §19 llevan `{limite}`, `{fecha}`…: se rellenan con lo que dice el servidor. */
function rellenar(texto: string, valores: Readonly<Record<string, string | number>>): string {
  return texto.replace(/\{(\w+)\}/g, (todo, clave: string) => (clave in valores ? String(valores[clave]) : todo));
}

/** La fecha en Madrid, que es la que el usuario entiende como «el día». */
function fechaLarga(iso: string): string {
  return new Intl.DateTimeFormat("es-ES", { timeZone: "Europe/Madrid", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));
}

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "oculta" }
  | { readonly tipo: "error" }
  | { readonly tipo: "datos"; readonly vista: VistaDeBajada };

export function TarjetaBajadaAFree({ hayConexion }: { readonly hayConexion: boolean }) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [elegidos, setElegidos] = useState<readonly string[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<{ readonly tono: "exito" | "error"; readonly texto: string } | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.bajadaAFree();
    if (r.ok) {
      setEstado({ tipo: "datos", vista: r.datos });
      setElegidos(r.datos.bajada?.seleccion ?? []);
      return;
    }
    // 403: no es OWNER. 404: una API sin esta ruta todavía. Ninguno es un error que enseñar.
    setEstado(r.estado === 403 || r.estado === 404 ? { tipo: "oculta" } : { tipo: "error" });
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (estado.tipo === "oculta" || estado.tipo === "cargando") return null;
  if (estado.tipo === "error") {
    return (
      <Tarjeta titulo={tx.titulo}>
        <Aviso tono="error">{tx.error}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>
          {t.plan.reintentar}
        </Boton>
      </Tarjeta>
    );
  }

  const { bajada, ultima } = estado.vista;

  if (bajada === null) {
    if (ultima === null) return null;
    if (ultima.restauracion === "HECHA") {
      return (
        <Tarjeta titulo={tx.tituloDespues}>
          <Aviso tono="exito">{tx.despuesDelUpgrade}</Aviso>
        </Tarjeta>
      );
    }
    if (ultima.selectionMode === "FALLBACK" && ultima.restauracion === "NINGUNA") {
      return (
        <Tarjeta titulo={tx.titulo}>
          <Aviso tono="info">{rellenar(tx.despuesDelFallback, { limite: ultima.limite ?? "" })}</Aviso>
          <p className="ayuda-neutra">{tx.noRotacion}</p>
        </Tarjeta>
      );
    }
    return null;
  }

  const fecha = fechaLarga(bajada.effectiveAt);
  const limite = bajada.limite ?? bajada.activos;
  const valores = { fecha, limite, activos: bajada.activos, n: elegidos.length };
  const demasiados = elegidos.length > limite;

  async function guardar(): Promise<void> {
    setMensaje(null);
    setOcupado(true);
    const r = await api.guardarSeleccionDeBajada(elegidos);
    setOcupado(false);
    if (r.ok) {
      setElegidos(r.datos);
      setMensaje({ tono: "exito", texto: rellenar(tx.guardada, valores) });
      return;
    }
    const motivo = r.error?.motivo ?? null;
    if (motivo === "SELECTION_FROZEN" || motivo === "NO_DOWNGRADE_SCHEDULED") {
      // El corte ha empezado (o la bajada se ha anulado): se relee para enseñar lo que hay de verdad.
      void cargar();
      setMensaje({ tono: "error", texto: tx.congelada });
      return;
    }
    setMensaje({ tono: "error", texto: motivo === "TOO_MANY" ? rellenar(tx.demasiados, valores) : tx.errorGuardar });
  }

  const marcar = (id: string, si: boolean): void => {
    setMensaje(null);
    setElegidos((antes) => (si ? [...antes.filter((x) => x !== id), id] : antes.filter((x) => x !== id)));
  };

  return (
    <Tarjeta titulo={tx.titulo}>
      <p>{rellenar(tx.antes, valores)}</p>
      {!bajada.sobran ? (
        <>
          <p>{rellenar(tx.caben, valores)}</p>
          <p className="ayuda-neutra">{tx.noRotacion}</p>
        </>
      ) : (
        <>
          <p>{rellenar(tx.permite, valores)}</p>
          {bajada.congelada ? (
            <Aviso tono="info">{tx.congelada}</Aviso>
          ) : (
            <>
              <p>{tx.selecciona}</p>
              <p className="ayuda-neutra">{tx.losDemas}</p>
              <p className="ayuda-neutra">{tx.puedesCambiar}</p>
            </>
          )}
          <Aviso tono="aviso">{tx.noRotacion}</Aviso>
          {!bajada.congelada ? (
            <>
              <p className="ayuda-neutra">{rellenar(tx.fallback, valores)}</p>
              <p aria-live="polite">{rellenar(tx.elegidos, valores)}</p>
              {bajada.candidatos.map((c) => (
                <Casilla
                  key={c.customerId}
                  id={`bajada-${c.customerId}`}
                  etiqueta={`${c.displayName} · ${
                    c.lastActivityAt === null ? tx.sinActividad : rellenar(tx.ultimaActividad, { fecha: fechaLarga(c.lastActivityAt) })
                  }`}
                  marcada={elegidos.includes(c.customerId)}
                  onChange={(si) => marcar(c.customerId, si)}
                />
              ))}
              {demasiados ? <Aviso tono="error">{rellenar(tx.demasiados, valores)}</Aviso> : null}
              <div className="acciones">
                <Boton onClick={() => void guardar()} deshabilitado={ocupado || demasiados || !hayConexion}>
                  {ocupado ? tx.guardando : tx.guardar}
                </Boton>
              </div>
            </>
          ) : null}
        </>
      )}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
    </Tarjeta>
  );
}
